// Copied from the nowadays repo, packages/ui/src/components/button.tsx at 6da58398f. Change it there first, then copy it here, rewriting any em dash (this repo bans them).
"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import {
  Button as ButtonPrimitive,
  Link as LinkPrimitive,
  type ButtonProps as ButtonPrimitiveProps,
  type LinkProps as LinkPrimitiveProps,
} from "react-aria-components"

import { cn } from "@/nowadays-ui/lib/utils"
import { Spinner } from "@/nowadays-ui/components/spinner"

const buttonVariants = cva(
  "group/button relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-4xl border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none data-pending:cursor-default focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 active:not-aria-[haspopup]:scale-98 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-invalid:border-destructive data-invalid:ring-3 data-invalid:ring-destructive/20 dark:data-invalid:border-destructive/50 dark:data-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        outline:
          "border-border-control bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:bg-transparent dark:hover:bg-input/30",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-9 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-6 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        lg: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * @description The system's button. Pass React Aria's `isPending` for a loading
 *   state: the label goes invisible and a spinner takes its place *without*
 *   changing the button's width, so nothing around it moves. React Aria
 *   supplies the rest, press is blocked, focus and hover are kept (unlike
 *   `isDisabled`, which drops focus to the page), a submit button stops
 *   submitting, and the change is announced.
 * @param {boolean} [isPending] - Whether an action triggered by this button is
 *   still running. Prefer it over `isDisabled` for in-flight requests.
 * @param {boolean} [isInvalid] - Whether what this button stands for is being
 *   refused. Use it rather than `aria-invalid`, which cannot reach a button.
 */
// DEVIATION: `forwardRef` is ours. Upstream runs on React 19, where `ref` is an
// ordinary prop on a function component. This repo is on React 18, which strips
// `ref` before a plain function sees it, so `TooltipTrigger`'s `Focusable` (and
// any caller holding a ref) got nothing and logged a warning. Remove the wrapper
// when this repo moves to React 19.
const Button = React.forwardRef<
  HTMLButtonElement,
  Omit<ButtonPrimitiveProps, "className"> &
    VariantProps<typeof buttonVariants> & {
      className?: string
      isInvalid?: boolean
    }
>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    isInvalid,
    children,
    ...props
  },
  ref
) {
  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      // DEVIATION: `isInvalid` and `data-invalid` are ours. Upstream styles the
      // refused state with `aria-invalid:` variants, which can never fire here:
      // React Aria runs a `Button`'s props through `filterDOMProps` and keeps
      // only the labelable `aria-*` attributes, so `aria-invalid` is dropped
      // before it reaches the DOM however it is passed. `select.tsx` records the
      // same finding for its trigger. The upstream declarations are left in
      // place so a re-vendor is a clean diff; the `data-invalid:` pair beside
      // them is what actually paints, the way `switch.tsx`, `checkbox.tsx` and
      // `radio-group.tsx` already carry both. A caller still owes the reader a
      // message: `aria-describedby` does survive the filter.
      data-invalid={isInvalid || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {(renderProps) => (
        <>
          {/* gap-[inherit] keeps the button's own icon/label spacing, so
              wrapping the content changes nothing about how it lays out. */}
          <span
            data-slot="button-content"
            className={cn(
              "inline-flex items-center gap-[inherit]",
              renderProps.isPending && "invisible"
            )}
          >
            {typeof children === "function" ? children(renderProps) : children}
          </span>
          {renderProps.isPending ? <Spinner className="absolute" /> : null}
        </>
      )}
    </ButtonPrimitive>
  )
})

// DEVIATION: `forwardRef` is ours, for the same React 18 reason as `Button`.
const LinkButton = React.forwardRef<
  HTMLAnchorElement,
  Omit<LinkPrimitiveProps, "className"> &
    VariantProps<typeof buttonVariants> & {
      className?: string
    }
>(function LinkButton(
  { className, variant = "default", size = "default", ...props },
  ref
) {
  return (
    <LinkPrimitive
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})

export { Button, LinkButton, buttonVariants }
